import { IsAny } from 'ts-essentials';
import { ResultWrapper } from './ResultWrapper';
import type { ReasonType } from './ReasonType';

export interface _Bad<TReason extends ReasonType> {
  success: false;
  hasValue: false;
  reason: TReason;
}

type DistributeBad<TReason extends ReasonType> = TReason extends ReasonType ? _Bad<TReason> : never;

export type Bad<TReason extends ReasonType> = DistributeBad<TReason>;

export interface _BadVal<TReason extends ReasonType, TValue> {
  success: false;
  hasValue: true;
  reason: TReason;
  value: TValue;
}

type DistributeBadVal<TReason extends ReasonType, TValue> = TReason extends ReasonType
  ? _BadVal<TReason, TValue>
  : never;

export type BadVal<TReason extends ReasonType, TValue> = DistributeBadVal<TReason, TValue>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type BadResult<R extends ReasonType = ReasonType, T = any> = Bad<R> | BadVal<R, T>;

export type ForbidAny<TReason> = IsAny<TReason> extends true ? never : TReason;

/**
 * Returns a failed result with a typed string or number reason.
 * @param reason A typed string or number reason describing why the operation has failed.
 * @param value
 */
function bad<TReason extends ReasonType>(reason: ForbidAny<TReason>, value?: undefined): Bad<TReason>;

function bad<TReason extends ReasonType, TValue>(
  reason: ForbidAny<TReason>,
  value: TValue
): BadVal<TReason, TValue>;

function bad<TReason extends ReasonType, TValue>(
  reason: ForbidAny<TReason>,
  value?: TValue
): BadVal<TReason, TValue> | Bad<TReason> {
  if (value !== undefined) {
    return new ResultWrapper({
      success: false as const,
      reason,
      value,
    }) as BadVal<TReason, TValue>;
  } else {
    return new ResultWrapper({
      success: false as const,
      reason,
    }) as Bad<TReason>;
  }
}

/**
 * A "Bad" result represents an operation that has failed, whose origin is known or which was predictable.
 * The result contains a reason describing why the operation has failed.
 * Note: any exceptional errors that cannot be compensated should not be represented as a result, but thrown as an exception (e.g.: a database transaction has failed, a request failed due to a network error, etc).
 */
export const Bad = bad;
